import styled, { css } from "styled-components";
import { designSystemVars as vars } from "../tokens";
import { ButtonBase } from "./Button";

const sharedCellFocusStyles = css`
  position: relative;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    z-index: 1;
    outline: none;
    box-shadow: inset 0 0 0 2px ${vars.color.action.primary};
  }
`;

export const TableHeaderIconButton = styled(ButtonBase).attrs({
  type: "button",
})`
  all: unset;
  box-sizing: border-box;
  display: flex;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  gap: ${vars.spacing.sm};
  border-radius: ${vars.radius.sm};
  background: var(--inzeloop-ds-color-background-transparent);
  color: ${vars.color.text.primary};
  cursor: default;

  svg [data-sort-active="false"] {
    opacity: 0.65;
  }

  svg [data-sort-active="true"] {
    opacity: 1;
  }

  &:hover {
    background: var(--inzeloop-ds-color-background-transparent);
    color: inherit;
  }

  &&:focus,
  &&:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

const sharedHeaderHoverStyles = css<{ onClick?: unknown }>`
  &${({ onClick }) =>
    typeof onClick === "function"
      ? ""
      : ':has(button, a[href], input, select, [role="button"])'}:hover {
    background: ${vars.color.action.primary};
    color: ${vars.color.text.inverse};

    ${TableHeaderIconButton},
    > a {
      background: var(--inzeloop-ds-color-background-transparent);
      color: inherit;
    }

    > svg [stroke]:not([stroke="none"]),
    ${TableHeaderIconButton} svg [stroke]:not([stroke="none"]),
    > a svg [stroke]:not([stroke="none"]) {
      stroke: currentColor;
    }

    > svg [fill]:not([fill="none"]),
    ${TableHeaderIconButton} svg [fill]:not([fill="none"]),
    > a svg [fill]:not([fill="none"]) {
      fill: currentColor;
    }

    > svg [data-sort-active="false"],
    ${TableHeaderIconButton} svg [data-sort-active="false"],
    > a svg [data-sort-active="false"] {
      opacity: 0.65;
    }

    > svg [data-sort-active="true"],
    ${TableHeaderIconButton} svg [data-sort-active="true"],
    > a svg [data-sort-active="true"] {
      opacity: 1;
    }
  }
`;

const sharedTableControlTypography = css`
  button,
  input,
  select,
  textarea {
    font-family: ${vars.typography.family.sans};
  }
`;

export const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  border: ${vars.borderWidth.thin} solid ${vars.color.border.subtle};
  border-radius: ${vars.radius.sm};
  background: ${vars.color.background.surface};
`;

export const Table = styled.table<{ $minWidth?: string }>`
  width: 100%;
  min-width: ${({ $minWidth }) => $minWidth || "640px"};
  border-spacing: 0;
  border-collapse: separate;
  color: ${vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.sm};
  line-height: ${vars.typography.lineHeight.sm};
  ${sharedTableControlTypography}
`;

export const TableCaption = styled.caption`
  padding: ${vars.spacing.md} ${vars.spacing.lg};
  color: ${vars.color.text.secondary};
  font-weight: ${vars.typography.weight.semibold};
  text-align: left;
`;

export const TableHead = styled.thead`
  background: ${vars.color.background.subtle};
`;

export const TableBody = styled.tbody``;

export const TableRow = styled.tr`
  &:not(:last-child) > * {
    border-bottom: ${vars.borderWidth.thin} solid ${vars.color.border.subtle};
  }
`;

export const TableHeaderCell = styled.th`
  padding: ${vars.spacing.md} ${vars.spacing.lg};
  color: ${vars.color.text.secondary};
  font-weight: ${vars.typography.weight.semibold};
  text-align: left;
  vertical-align: middle;
  ${sharedCellFocusStyles}
  ${sharedHeaderHoverStyles}
`;

export const TableCell = styled.td`
  padding: ${vars.spacing.md} ${vars.spacing.lg};
  vertical-align: middle;
  ${sharedCellFocusStyles}
`;

export const TableEmptyCell = styled(TableCell)`
  padding: ${vars.spacing["2xl"]} ${vars.spacing.lg};
  color: ${vars.color.text.muted};
  text-align: center;
`;

export const DataGrid = styled.div`
  box-sizing: border-box;
  width: 100%;
  color: ${vars.color.text.primary};
  font-family: ${vars.typography.family.sans};
  font-size: ${vars.typography.size.sm};
  line-height: ${vars.typography.lineHeight.sm};
  ${sharedTableControlTypography}
`;

export const DataGridRowGroup = styled.div`
  box-sizing: border-box;
  width: 100%;
`;

export const DataGridRow = styled.div`
  box-sizing: border-box;
  display: flex;
  width: 100%;
`;

export const DataGridHeaderCell = styled.div`
  box-sizing: border-box;
  min-width: 0;
  ${sharedCellFocusStyles}
  ${sharedHeaderHoverStyles}
`;

export const DataGridCell = styled.div`
  box-sizing: border-box;
  min-width: 0;
  ${sharedCellFocusStyles}
`;
